// @ts-nocheck
import { Add, Category, CodeOutlined, DateRange, Voicemail } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useFieldArray, useForm } from 'react-hook-form';
import { EditableCard } from 'src/careflow/common/EditableCard';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { useConditions, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useEffect, useState, useMemo } from 'react';
import CancelButton from 'src/careflow/common/CancelButton';
import useLocales, {
  i18nFunction,
} from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  ConditionWrapper,
  WrappedCondition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useObjectState from 'src/hooks/useObjectState';
import LoadableAutocomplete from 'src/components/LoadableAutocomplete';
import { getReferenceId } from 'src/utils/fhir';
import { Condition, EpisodeOfCareDiagnosis } from 'src/careflow/ph-core/fhir-client/resources';
import Iconify from 'src/components/Iconify';
import { periodFormatted } from 'src/utils/formatTime';
import { WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';

type DiagnosisForm = {
  id?: string;
  code?: Condition['code'];
  recorder?: Condition['recorder'];
  onsetPeriod?: Condition['onsetPeriod'];
  category?: Condition['category'];
  rank?: number;
  role?: EpisodeOfCareDiagnosis['role'];
};

function Diagnosis({
  eoc,
  patient,
  handle,
}: {
  eoc: WrappedEpisodeOfCare | null;
  patient?: WrappedPatient | null;
  handle: Function;
}) {
  const { i18n } = useLocales();
  const [conditions] = useConditions({
    filter: { patient: patient?.id },
    map: ConditionWrapper,
    autofetch: !!patient?.id,
  });
  const [diagnosisRoleVS] = useValueSet('diagnosis-role', {
    map: ValueSetWrapper,
  });

  const [open, setOpen] = useState(false);
  const [moved, setMoved] = useState(false);
  const [cancel, setCancel] = useState(false);

  const methods = useForm({
    defaultValues: {
      diagnosis: [],
    },
  });

  const { control } = methods;

  const { replace } = useFieldArray({
    control,
    name: 'diagnosis',
  });

  const { matched, unmatched } = useMemo(() => {
    const diagnosisMap = new Map(
      (eoc?.diagnosis ?? [])
        .filter((d) => d.condition?.reference)
        .map((d) => [getReferenceId(d.condition!.reference), d])
    );

    const matched: DiagnosisForm[] = conditions
      .filter((condition) => diagnosisMap.has(condition.id))
      .map((condition) => {
        const diagnosis = diagnosisMap.get(condition.id)!;

        return {
          id: condition.id,
          code: condition.code,
          recorder: condition.recorder,
          onsetPeriod: condition.onsetPeriod,
          category: condition.category,
          rank: diagnosis.rank,
          role: diagnosis.role,
        };
      });

    const unmatched: WrappedCondition[] = conditions.filter(
      (condition) => !diagnosisMap.has(condition.id)
    );

    return { matched, unmatched };
  }, [eoc, conditions]);

  useEffect(() => {
    if (matched || cancel) {
      replace(
        matched?.map((item, index) => ({
          ...item,
          rank: item.rank ?? index + 1,
        }))
      );
      setCancel(false);
    }
  }, [matched, cancel, replace]);

  const diagnosis = methods.getValues('diagnosis');

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || source.index === destination.index) return;

    const updated = [...diagnosis];
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);
    setMoved(true);
    replace(
      updated.map((item, index) => ({
        ...item,
        rank: index + 1,
      }))
    );
  };

  const onReset = () => {
    setMoved(false);
    setCancel(false);
    setOpen(false);
  };

  const buildPayload = (diagnosis: EpisodeOfCareDiagnosis[]) => ({
    ...eoc,
    diagnosis,
  });

  const handleAdd = async (data: DiagnosisForm) => {
    const diagnosisUpdated = [
      ...(eoc?.diagnosis ?? []),
      {
        ...data,
        rank: (eoc?.diagnosis?.length ?? 0) + 1,
      },
    ];

    handle(buildPayload(diagnosisUpdated));
    onReset();
  };

  const handleMove = async () => {
    const diagnosisUpdated = diagnosis.map((item) => ({
      condition: {
        reference: `Condition/${item.id}`,
      },
      role: item.role,
      rank: item.rank,
    }));

    handle(buildPayload(diagnosisUpdated));
    onReset();
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <EditableCard
            title={i18n('episodeOfCare.details.diagnosis.title', 'crs')}
            showEditButton={false}
            button={
              moved ? (
                <Stack direction={{ sm: 'row' }} justifyContent="flex-end" sx={{ mt: -1, ml: 2 }}>
                  <Tooltip title={i18n('saveChanges')}>
                    <IconButton onClick={handleMove}>
                      <Iconify icon={'icon-park-solid:check-one'} color={'#3dab2b'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n('cancel')}>
                    <IconButton
                      onClick={() => {
                        setMoved(false);
                        setCancel(true);
                      }}
                    >
                      <Iconify icon={'carbon:close-filled'} color={'#ff4842'} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : null
            }
          >
            <Box sx={{ position: 'absolute', top: '20px', right: '25px' }}>
              <Button
                variant="contained"
                size="small"
                sx={{ height: '36px' }}
                startIcon={<Add />}
                onClick={() => setOpen(true)}
              >
                {i18n('episodeOfCare.details.diagnosis.add', 'crs')}
              </Button>
            </Box>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="diagnosis">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {diagnosis.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Box
                              sx={{
                                m: 2,
                                p: 2,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              }}
                              key={item.id}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                mb={2}
                              >
                                <Box>
                                  <Typography variant="subtitle1">
                                    {diagnosis[index]?.code?.text}
                                  </Typography>

                                  <Typography variant="body2" color="text.secondary">
                                    {i18n('episodeOfCare.details.diagnosis.role', 'crs')}:{' '}
                                    {diagnosis[index]?.role?.coding?.[0]?.display}
                                  </Typography>
                                </Box>

                                <Chip
                                  label={`Rank ${diagnosis[index]?.rank}`}
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  sx={{
                                    fontWeight: 700,
                                  }}
                                />
                              </Box>

                              <Box
                                display="flex"
                                alignItems="center"
                                gap={3}
                                pt={1.5}
                                borderTop={1}
                                borderColor="divider"
                              >
                                <Tooltip
                                  title={i18n('episodeOfCare.details.diagnosis.period', 'crs')}
                                >
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <DateRange fontSize="small" color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                      {periodFormatted(diagnosis[index]?.onsetPeriod)}
                                    </Typography>
                                  </Box>
                                </Tooltip>

                                <Tooltip
                                  title={i18n('episodeOfCare.details.diagnosis.code', 'crs')}
                                >
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CodeOutlined fontSize="small" color="warning" />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {diagnosis[index]?.code?.coding?.[0]?.code}
                                    </Typography>
                                  </Box>
                                </Tooltip>

                                <Tooltip
                                  title={i18n('episodeOfCare.details.diagnosis.category', 'crs')}
                                >
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Category fontSize="small" color="disabled" />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {diagnosis[index]?.category?.[0].coding?.[0]?.display}
                                    </Typography>
                                  </Box>
                                </Tooltip>

                                <Tooltip
                                  title={i18n('episodeOfCare.details.diagnosis.recorder', 'crs')}
                                >
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Voicemail fontSize="small" color="error" />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      {diagnosis[index]?.recorder?.display}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </Box>
                            </Box>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </EditableCard>
        </Grid>
      </Grid>
      <AddModal
        conditions={unmatched}
        diagnosisRoleVS={diagnosisRoleVS}
        open={open}
        save={handleAdd}
        handleClose={() => setOpen(false)}
        i18n={i18n}
      />
    </>
  );
}

const AddModal = ({
  conditions,
  diagnosisRoleVS,
  open,
  save,
  handleClose,
  i18n,
}: {
  conditions: WrappedCondition[] | null;
  diagnosisRoleVS: WrappedValueSet | null;
  open: boolean;
  save: Function;
  handleClose: VoidFunction;
  i18n: i18nFunction;
}) => {
  const [{ condition, role, errorCondition }, updateState] = useObjectState<any>({
    condition: null,
    role: null,
    errorCondition: false,
  });

  const saveChanges = async () => {
    if (!condition) {
      updateState({ errorCondition: true });
      return;
    }
    await save({
      condition: {
        reference: `Condition/${condition?.id}`,
      },
      ...(role && {
        role: {
          coding: [role],
        },
      }),
    });

    onClose();
  };

  const onClose = () => {
    updateState({
      condition: null,
      role: null,
      errorCondition: false,
    });
    handleClose();
  };

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>
        {i18n('episodeOfCare.details.diagnosis.add', 'crs')}
        {!conditions?.length && (
          <>
            <Typography ml={2} variant="caption" color="red">
              {i18n('episodeOfCare.details.diagnosis.noConditions', 'crs')}
            </Typography>
          </>
        )}
      </DialogTitle>
      <Card sx={{ m: 2 }}>
        <Stack spacing={2} m={2}>
          <LoadableAutocomplete
            fullWidth
            sx={{ mt: 1 }}
            label={`${i18n('episodeOfCare.details.diagnosis.condition', 'crs')} *`}
            isLoading={false}
            value={condition}
            onChange={(_, newValue) => {
              updateState({
                errorCondition: false,
                condition: newValue,
              });
            }}
            error={errorCondition && i18n('episodeOfCare.details.diagnosis.errorCondition', 'crs')}
            options={conditions ?? []}
            getOptionLabel={(e) => e?.getConditionCoding?.()?.display ?? ''}
          />
          <LoadableAutocomplete
            fullWidth
            sx={{ mt: 1 }}
            label={i18n('episodeOfCare.details.diagnosis.role', 'crs')}
            isLoading={false}
            value={role}
            onChange={(_, newValue) => {
              updateState({
                role: newValue,
              });
            }}
            options={diagnosisRoleVS?.asListAll() ?? []}
            getOptionLabel={(e) => e?.display ?? ''}
          />
        </Stack>
        <DialogActions>
          <CancelButton onClick={onClose}>{i18n('cancel')}</CancelButton>
          <Button variant="contained" onClick={saveChanges} disabled={!conditions?.length}>
            {i18n('saveChanges')}
          </Button>
        </DialogActions>
      </Card>
    </Dialog>
  );
};

export default Diagnosis;
